'use client';

import { useMemo } from 'react';
import clsx from 'clsx';

import { Link, usePathname } from '@northladder/i18n';

import { TIcon } from '../../icons';

import { CountBadge } from './CountBadge';

export interface IMenuItemBaseOptions {
  /**
   * Number of items in the menu item.
   */
  counts?: number;
  /**
   * The active navigation path of the route.
   */
  href: string;
  /**
   * Icon of the menu item.
   */
  Icon: TIcon;
  /**
   * Used to test an exact match of the route.
   */
  isExact?: boolean;
  /**
   * Title of the menu item.
   */
  title: string;
}

interface ISidebarMenuItemProps {
  item: IMenuItemBaseOptions;
  /**
   * The menu item is expanded by default.
   */
  isExpanded?: boolean;
  /**
   * It is called when the menu item is clicked.
   */
  onClick?: (id: string | number) => void;
}

/**
 * -----------------------------------------------------------------------------
 * It renders a menu item with a title, an icon and with a number badge.
 */
export function SidebarMenuItem({
  isExpanded,
  item,
}: Readonly<ISidebarMenuItemProps>) {
  const pathName = usePathname() || '';

  const { counts = 0, href, Icon, isExact, title } = item;

  /**
   * This is used to determine if the menu item is active or not, and also make
   * sure it matches netsted routes like `/app/settings` and `app/settings/profile`.
   */
  const isActive = useMemo(() => {
    if (isExact) return pathName === href;

    return pathName.startsWith(href);
  }, [isExact, pathName, href]);

  return (
    <Link
      className={clsx(
        `
          flex cursor-pointer items-center gap-4 rounded-xl
          border border-transparent py-3 text-xs transition-all duration-1000 ease-in-out
          hover:border-green-500/10 hover:bg-green-50 hover:fill-green-500
          hover:stroke-green-600 hover:text-green-600
        `,
        isActive
          ? 'bg-gray-50 fill-green-700 stroke-green-800/50 font-medium text-green-800'
          : 'bg-transparent fill-none stroke-gray-800 text-gray-800',
        isExpanded ? 'px-4' : 'mx-2 flex items-center justify-center'
      )}
      href={href}
    >
      <div className="min-w-6">
        <div className="relative" title={title}>
          <Icon strokeWidth={1.25} variant={isActive ? 'solid' : 'outlined'} />
          {counts ? (
            <div
              className={clsx(
                'absolute right-0 top-0',
                isExpanded ? 'hidden' : ''
              )}
              // TODO: fix the positioning with the classes
              style={{ transform: 'translate(50%, -50%)' }}
            >
              <CountBadge counts={counts} />
            </div>
          ) : null}
        </div>
      </div>

      <div
        className={clsx(
          'items-center gap-4 transition-all duration-500 ease-in-out',
          isExpanded ? 'flex w-fit opacity-100' : 'hidden w-0 opacity-0'
        )}
      >
        <span>{title}</span>
        {counts ? (
          <div>
            <CountBadge counts={counts} />
          </div>
        ) : null}
      </div>
    </Link>
  );
}
